import React, { useEffect, useRef, useState } from 'react';
import { Check, Crown, Wand2, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { siteData, getWhatsAppUrl } from '../data/mock';

export const Services = () => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleCta = (service) => {
    if (service.ctaAction === 'whatsapp') {
      const msg = "Hi! I'm interested in a Custom WedSite design. Can we discuss my requirements?";
      window.open(getWhatsAppUrl(msg), '_blank');
    } else {
      document.querySelector(service.ctaAction)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="services" ref={ref} className="py-24 lg:py-32 bg-slate-50 relative overflow-hidden">
      {/* Background Decorative Blobs */}
      <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 bg-vibrant-purple/10 rounded-full blur-3xl opacity-50" />
      <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 bg-vibrant-blue/10 rounded-full blur-3xl opacity-50" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className={`max-w-3xl mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="inline-block px-3 py-1 mb-4 text-xs font-semibold tracking-wider text-vibrant-blue uppercase bg-blue-50 rounded-full border border-blue-100">
            Our Packages
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold leading-tight text-slate-900 mb-6 font-outfit">
            Choose Your Perfect <span className="text-transparent bg-clip-text bg-gradient-to-r from-vibrant-blue to-vibrant-purple">WedSite</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl leading-relaxed">
            From ready-made templates to fully custom designs — we have the perfect option for every couple.
          </p>
        </div>

        {/* Service Cards */}
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12 max-w-5xl">
          {siteData.services.map((service, index) => (
            <div
              key={service.id}
              className={`group relative rounded-[2.5rem] p-8 lg:p-12 transition-all duration-500 modern-card ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                } ${service.popular
                  ? 'bg-white shadow-2xl shadow-vibrant-red/10 border-2 border-vibrant-red/5'
                  : 'bg-white shadow-xl shadow-slate-200/50 border border-slate-100'
                }`}
              style={{ transitionDelay: `${index * 200}ms` }}
            >
              {service.popular && (
                <div className="absolute -top-4 right-10">
                  <Badge className="rounded-full px-4 py-1.5 text-xs font-bold border-0 bg-gradient-to-r from-vibrant-red to-vibrant-pink text-white shadow-lg shadow-vibrant-red/25">
                    <Crown className="w-3.5 h-3.5 mr-1.5 fill-current" /> MOST POPULAR
                  </Badge>
                </div>
              )}

              <div
                className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-8 transition-transform duration-500 group-hover:scale-110 ${service.popular ? 'bg-red-50 text-vibrant-red' : 'bg-emerald-50 text-vibrant-green'
                  }`}
              >
                {service.popular
                  ? <Crown className="w-8 h-8" />
                  : <Wand2 className="w-8 h-8" />
                }
              </div>

              <h3 className="text-3xl font-bold mb-3 text-slate-900 font-outfit">
                {service.title}
              </h3>
              <div className={`text-4xl font-extrabold mb-6 tracking-tight ${service.popular ? 'text-vibrant-red' : 'text-vibrant-green'
                }`}>
                {service.price}
              </div>
              <p className="mb-10 text-slate-600 leading-relaxed text-lg">
                {service.description}
              </p>

              <ul className="space-y-4 mb-12">
                {service.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${service.popular ? 'bg-red-100/50 text-vibrant-red' : 'bg-emerald-100/50 text-vibrant-green'
                      }`}>
                      <Check className="w-3.5 h-3.5 stroke-[3]" />
                    </div>
                    <span className="text-slate-700 font-medium">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                className={`w-full h-14 rounded-full text-lg font-semibold transition-all duration-300 shadow-xl ${service.popular
                    ? 'bg-slate-900 text-white hover:bg-slate-800 hover:shadow-slate-900/20'
                    : 'bg-white text-slate-900 border-2 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  } group-hover:scale-[1.02]`}
                onClick={() => handleCta(service)}
              >
                {service.ctaText} <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
